import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Project } from '../../types/project';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProjectCardProps {
  project: Project;
  onSelect: (project: Project) => void;
  isMobile: boolean;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onSelect,
  isMobile,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const cardVariants = {
    hover: !isMobile
      ? {
          y: -4,
          transition: {
            type: 'spring',
            stiffness: 300,
            damping: 20,
          },
        }
      : {},
  };

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current && e.deltaY !== 0) {
      e.preventDefault();
      const scrollSpeed = 150; // Adjust this value to change scroll speed
      scrollContainerRef.current.scrollLeft +=
        e.deltaY > 0 ? scrollSpeed : -scrollSpeed;
      setIsScrolling(true);
      checkScroll();

      // Reset scrolling state after animation
      setTimeout(() => setIsScrolling(false), 150);
    }
  };

  const handleScroll = () => {
    checkScroll();
    setIsScrolling(true);
    // Reset scrolling state after animation
    setTimeout(() => setIsScrolling(false), 150);
  };

  const scrollTo = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 100; // Adjust this value to change scroll amount
      const targetScroll =
        scrollContainerRef.current.scrollLeft +
        (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: 'smooth',
      });
    }
  };

  const handleTechClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Check scroll possibilities on mount and after content changes
  React.useEffect(() => {
    checkScroll();
  }, [project.technologies]);

  return (
    <motion.div
      onClick={() => onSelect(project)}
      variants={cardVariants}
      whileHover="hover"
      whileTap={isMobile ? { scale: 0.98 } : undefined}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg cursor-pointer h-64"
    >
      <div className="relative h-36">
        <motion.img
          whileHover={!isMobile ? { scale: 1.05 } : undefined}
          transition={{ duration: 0.2 }}
          src={project.imageUrl}
          alt={`${project.title} thumbnail`}
          className="absolute inset-0 w-full h-full object-contain"
          loading="lazy"
        />
      </div>
      <div className="p-3 flex flex-col h-28">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
          {project.title}
        </h3>
        {/* Add description/overview section */}
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-1 leading-relaxed">
          {project.description.overview ||
            // Fallback to old description structure if needed
            (typeof project.description === 'string'
              ? project.description
              : '')}
        </p>
        <div className="mt-auto" onClick={handleTechClick}>
          <div className="relative w-full group">
            {/* Left scroll button */}
            {canScrollLeft && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  scrollTo('left');
                }}
                className="absolute left-0 top-1/2 -translate-y-2/3 -translate-x-1/2  z-10 p-1 
                  bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600
                  rounded-full shadow-md opacity-0 group-hover:opacity-100 
                  transition-all duration-200 border border-gray-200 dark:border-gray-600"
              >
                <ChevronLeft className="w-3 h-3 text-gray-600 dark:text-gray-200" />
              </button>
            )}

            {/* Right scroll button */}
            {canScrollRight && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  scrollTo('right');
                }}
                className="absolute right-0 top-1/2 -translate-y-2/3 translate-x-1/2  z-10 p-1
                bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600
                rounded-full shadow-md opacity-0 group-hover:opacity-100 
                transition-all duration-200 border border-gray-200 dark:border-gray-600"
              >
                <ChevronRight className="w-3 h-3 text-gray-600 dark:text-gray-200" />
              </button>
            )}

            {/* Gradient overlays */}
            {canScrollLeft && (
              <div
                className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white dark:from-gray-800 to-transparent pointer-events-none 
                opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              />
            )}
            {canScrollRight && (
              <div
                className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white dark:from-gray-800 to-transparent pointer-events-none 
                opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              />
            )}

            <div
              ref={scrollContainerRef}
              onWheel={handleWheel}
              onScroll={handleScroll}
              className={`flex gap-1 overflow-x-auto scrollbar-hide pb-1 snap-x snap-mandatory touch-pan-x
                ${canScrollLeft || canScrollRight ? 'hover:cursor-ew-resize' : ''}
                ${isScrolling ? 'transition-transform duration-150 ease-out' : ''}`}
              style={{
                WebkitOverflowScrolling: 'touch',
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth',
              }}
            >
              {project.technologies.map((tech, index) => (
                <motion.span
                  key={index}
                  initial={false}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                  className={`flex-none px-2 py-0.5 ${tech.colorClass} rounded-full text-xs whitespace-nowrap snap-start
                    hover:shadow-md transition-shadow duration-200`}
                >
                  {tech.name}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
